import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getCurrentUser, signOut } from "../../supabase/auth";
import { supabase } from "../../supabase/supabaseClient";
import { VscSearch, VscHome, VscCompass, VscMail, VscStarFull, VscCalendar, VscColorMode, VscBell } from "react-icons/vsc";
import { useNotifications } from "../../context/NotificationContext";
import { listConversations, listUnreadByConversation } from "../../services/messagesService";
import "./NavBar.css";

function NavBar() {
    const { unreadCount } = useNotifications();
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [theme, setTheme] = useState(() => localStorage.getItem("app-theme") || "dark");
    const menuRef = useRef(null);

    const resolveUserRoleAndAvatar = async (currentUser) => {
        if (!currentUser) {
            return { role: null, avatar: null };
        }

        const { data: upData } = await supabase
            .from("user_profiles")
            .select("role, avatar_url")
            .eq("user_id", currentUser.id)
            .maybeSingle();

        const { data: playerData } = await supabase
            .from("player_profiles")
            .select("avatar_url")
            .eq("user_id", currentUser.id)
            .maybeSingle();

        return {
            role: upData?.role?.toLowerCase() || currentUser?.user_metadata?.role?.toLowerCase() || null,
            avatar: upData?.avatar_url || playerData?.avatar_url || currentUser?.user_metadata?.avatar_url || null,
        };
    };

    useEffect(() => {
        const storedTheme = localStorage.getItem("app-theme");
        if (storedTheme) {
            setTheme(storedTheme);
            return;
        }

        const prefersLight = window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches;
        setTheme(prefersLight ? "light" : "dark");
    }, []);

    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
        localStorage.setItem("app-theme", theme);
    }, [theme]);

    useEffect(() => {
        let isMounted = true;

        const loadFromSession = async () => {
            try {
                const { data } = await supabase.auth.getSession();
                if (!isMounted) return;
                const currentUser = data?.session?.user ?? null;
                setUser(currentUser);
                
                // Fetch role from user_profiles
                if (currentUser) {
                    try {
                        const identity = await resolveUserRoleAndAvatar(currentUser);
                        if (isMounted) {
                            setUserRole(identity.role);
                            setAvatarUrl(identity.avatar);
                        }
                    } catch (err) {
                        console.error("NavBar: error fetching user_profiles role", err);
                        if (isMounted) {
                            setUserRole(currentUser?.user_metadata?.role?.toLowerCase() || null);
                            setAvatarUrl(currentUser?.user_metadata?.avatar_url || null);
                        }
                    }
                } else {
                    if (isMounted) {
                        setUserRole(null);
                        setAvatarUrl(null);
                    }
                }
            } catch {
                // If anything goes wrong, fall back to the older getCurrentUser() path.
                const { data } = await getCurrentUser();
                if (!isMounted) return;
                const currentUser = data?.user ?? null;
                setUser(currentUser);
                
                if (currentUser) {
                    try {
                        const identity = await resolveUserRoleAndAvatar(currentUser);
                        if (isMounted) {
                            setUserRole(identity.role);
                            setAvatarUrl(identity.avatar);
                        }
                    } catch (err) {
                        console.error("NavBar: error fetching user_profiles role", err);
                        if (isMounted) {
                            setUserRole(currentUser?.user_metadata?.role?.toLowerCase() || null);
                            setAvatarUrl(currentUser?.user_metadata?.avatar_url || null);
                        }
                    }
                } else {
                    if (isMounted) {
                        setUserRole(null);
                        setAvatarUrl(null);
                    }
                }
            }
        };

        loadFromSession();

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            if (!isMounted) return;
            const currentUser = session?.user ?? null;
            setUser(currentUser);
            
            if (currentUser) {
                resolveUserRoleAndAvatar(currentUser)
                    .then((identity) => {
                        if (isMounted) {
                            setUserRole(identity.role);
                            setAvatarUrl(identity.avatar);
                        }
                    })
                    .catch((err) => {
                        console.error("NavBar: error fetching user_profiles role", err);
                        if (isMounted) {
                            setUserRole(currentUser?.user_metadata?.role?.toLowerCase() || null);
                            setAvatarUrl(currentUser?.user_metadata?.avatar_url || null);
                        }
                    });
            } else {
                if (isMounted) {
                    setUserRole(null);
                    setAvatarUrl(null);
                }
            }
        });

        return () => {
            isMounted = false;
            subscription?.unsubscribe();
        };
        // Keep location as a dependency so the nav updates promptly on route changes,
        // but avoid network-calling getUser() on every route.
    }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleAvatarUpdated = (event) => {
            const nextAvatar = event?.detail?.avatarUrl || null;
            if (nextAvatar) {
                setAvatarUrl(nextAvatar);
            }
        };

        window.addEventListener("avatar-updated", handleAvatarUpdated);
        return () => window.removeEventListener("avatar-updated", handleAvatarUpdated);
    }, []);

    useEffect(() => {
        if (!user?.id) {
            setUnreadMessagesCount(0);
            return;
        }

        let isMounted = true;

        const loadUnreadMessageCount = async () => {
            try {
                const { data: conversations, error: convError } = await listConversations(user.id);
                if (convError || !conversations) {
                    if (isMounted) setUnreadMessagesCount(0);
                    return;
                }

                const conversationIds = conversations
                    .filter((conv) => conv.status === "accepted" || conv.status === "pending")
                    .map((conv) => conv.id);

                if (conversationIds.length === 0) {
                    if (isMounted) setUnreadMessagesCount(0);
                    return;
                }

                const { data: unreadRows, error: unreadError } = await listUnreadByConversation(user.id, conversationIds);
                if (unreadError || !unreadRows) {
                    if (isMounted) setUnreadMessagesCount(0);
                    return;
                }

                if (isMounted) {
                    setUnreadMessagesCount(unreadRows.length);
                }
            } catch (error) {
                console.error("NavBar: error loading unread message count", error);
                if (isMounted) setUnreadMessagesCount(0);
            }
        };

        loadUnreadMessageCount();
        const intervalId = window.setInterval(loadUnreadMessageCount, 20000);

        return () => {
            isMounted = false;
            window.clearInterval(intervalId);
        };
    }, [user?.id, location.pathname]);

    const handleLogout = async () => {
        const { error } = await signOut();
        if (!error) {
            setUser(null);
            navigate("/");
        } else {
            console.error("Error signing out", error);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const q = searchQuery.trim();
        if (q.length > 0) {
            navigate(`/explore?q=${encodeURIComponent(q)}`);
        } else {
            navigate("/explore");
        }
    };

    const isAuthPage = location.pathname === "/login" || location.pathname === "/signup";
    const nextThemeLabel = theme === "dark" ? "Light mode" : "Dark mode";
    const meInitial = ((user?.user_metadata?.first_name || user?.email || "U").charAt(0) || "U").toUpperCase();

    return (
        <header className="top-nav">
            <div className="top-nav-inner">
                <Link to="/" className="top-nav-left">
                    <img src="/DribblLogo.jpg" alt="Dribbl logo" className="top-nav-logo" />
                </Link>

                {user && !isAuthPage && (
                    <form className="top-nav-search" onSubmit={handleSearchSubmit}>
                        <span className="top-nav-search-icon">
                            <VscSearch size={16} />
                        </span>
                        <input
                            type="search"
                            className="top-nav-search-input"
                            placeholder="Search players, clubs, coaches..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            aria-label="Search players"
                        />
                    </form>
                )}

                <div className="top-nav-right">
                    {user ? (
                        <>
                            <Link to="/home" className="top-nav-item">
                                <VscHome size={20} />
                                <span>Home</span>
                            </Link>
                            <Link to="/explore" className="top-nav-item">
                                <VscCompass size={20} />
                                <span>Explore</span>
                            </Link>
                            <Link to="/events" className="top-nav-item">
                                <VscCalendar size={20} />
                                <span>Events</span>
                            </Link>
                            <Link to="/messages" className="top-nav-item top-nav-item-messaging" aria-label="Messaging">
                                <div className="top-nav-message-icon-wrap">
                                    <VscMail size={20} />
                                    {unreadMessagesCount > 0 && (
                                        <div className="top-nav-message-badge">{unreadMessagesCount > 9 ? "9+" : unreadMessagesCount}</div>
                                    )}
                                </div>
                                <span>Messaging</span>
                            </Link>
                            <Link to="/notifications" className="top-nav-item top-nav-item-notifications" aria-label="Notifications">
                                <div className="top-nav-notification-icon-wrap">
                                    <VscBell size={20} />
                                    {unreadCount > 0 && (
                                        <div className="top-nav-notification-badge">{unreadCount > 9 ? "9+" : unreadCount}</div>
                                    )}
                                </div>
                                <span>Notifications</span>
                            </Link>

                            {(userRole === "scout" || userRole === "coach") && (
                                <Link to="/scout/favorites" className="top-nav-item">
                                    <VscStarFull size={20} />
                                    <span>Favorites</span>
                                </Link>
                            )}
                            <div className="top-nav-me" ref={menuRef}>
                                <button
                                    type="button"
                                    className="top-nav-me-trigger"
                                    onClick={() => setMenuOpen((open) => !open)}
                                    aria-haspopup="menu"
                                    aria-expanded={menuOpen}
                                > 
                                    {avatarUrl ? (
                                        <img src={avatarUrl} alt="My profile" className="top-nav-me-avatar" />
                                    ) : (
                                        <span className="top-nav-me-avatar top-nav-me-avatar-fallback">{meInitial}</span>
                                    )} <span>Me</span>
                                </button>
                                {menuOpen && (
                                    <div className="top-nav-menu" role="menu">
                                        <Link to="/profile" className="top-nav-menu-item" role="menuitem">
                                            My profile
                                        </Link>
                                        {(userRole === "scout" || userRole === "coach" || userRole === "admin") && (
                                            <Link to="/org/dashboard" className="top-nav-menu-item" role="menuitem">
                                                Organization Dashboard
                                            </Link>
                                        )}
                                        <Link to="/settings" className="top-nav-menu-item" role="menuitem">
                                            Settings
                                        </Link>
                                        <button
                                            type="button"
                                            className="top-nav-menu-item"
                                            onClick={() => setTheme((current) => (current === "dark" ? "light" : "dark"))}
                                            role="menuitem"
                                        >
                                            <VscColorMode size={16} />
                                            <span>{nextThemeLabel}</span>
                                        </button>
                                        <button type="button" className="top-nav-menu-item" onClick={handleLogout} role="menuitem">
                                            Log out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : !isAuthPage ? (
                        <>
                        </>
                    ) : null}
                </div>
            </div>
        </header>
    );
}

export default NavBar;
